import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { LoyaltyFragmentFragmentDoc } from '@packages/gql/generated/shopping/LoyaltyFragmentFragmentDoc';
import { FormattedMessage, useIntl } from 'react-intl';
import { Card } from '@packages/shared/src/ui/Card/Card';

export type JoeLoyaltyProps = {
  loyaltyProgramData: FragmentType<typeof LoyaltyFragmentFragmentDoc>;
};

export const JoeLoyalty = ({ loyaltyProgramData }: JoeLoyaltyProps) => {
  const {
    order: { joe, externalUrls: { joe: joeUrl } = {} },
  } = useConfig();
  const { locale } = useIntl();
  const { basePoints, connected } = getFragmentData(LoyaltyFragmentFragmentDoc, loyaltyProgramData);

  if (!joe.enabled) {
    // Here we want to return no component if joe is not enabled for specific tenants
    return null;
  }
  if (typeof basePoints === 'undefined') {
    // Here we make the difference between undefined and null on basePoints because we want to show the component even if data is nullish
    return null;
  }
  return (
    <div className="w-[calc(100% + 1.5rem)] flex-col bg-white pb-1.5 md:w-full">
      <div>
        <div className="icon-emp-joe-logo xs:bg-grey-light flex size-6 w-full justify-center md:bg-transparent" />
        <h4 className="my-2 text-center">
          <FormattedMessage
            key="JoeLoyalty.Headline-text"
            defaultMessage="jö Vorteile"
            id="JoeLoyalty.Headline"
          />
        </h4>
      </div>
      <div className="bg-color-grey-light p-2">
        <Card className="p-1">
          <div className="flex flex-row">
            <div>
              <span className="icon-emp-joe-logo mr-2 size-6" />
            </div>
            <div className="flex grow flex-col">
              <div className="flex flex-row items-center justify-between gap-1">
                {/*
                  p: { xs: '0.875rem 0.75rem 1.75rem', md: '0.938rem 1rem 1.625rem' },
                  bgcolor: 'grey.light',
                */}
                <span className="text-size-body-sm justify-self-start font-semibold">
                  <FormattedMessage
                    key="JoeLoyalty.Points.Message-text"
                    id="JoeLoyalty.Points.Message"
                    defaultMessage="Werden gesammelt: "
                  />
                </span>
                <span className="text-size-body-sm flex items-center justify-self-end font-semibold text-nowrap">
                  <FormattedMessage
                    key="JoeLoyalty.Points.Value-text"
                    defaultMessage="{joePoints}"
                    values={{ joePoints: basePoints ?? '--' }} // Here we use nullish coalescense operator to show -- if basePoints are null so atleast something is displayed
                    id="JoeLoyalty.Points.Value"
                  />
                  <span className="icon-emp-joe ml-1 size-2.5" />
                </span>
              </div>

              <div className="text-size-body-xs text-color-text-darktransparent">
                {connected && (
                  <FormattedMessage
                    key="JoeLoyalty.Verified.Message-text"
                    id="JoeLoyalty.Verified.Message"
                    defaultMessage="Ihre persönlichen Coupons können auf Schritt 5 eingegeben werden."
                  />
                )}
              </div>
            </div>
          </div>
          {!connected && (
            <>
              <div className="text-size-body-sm mt-2 mb-0.5 font-semibold">
                <FormattedMessage
                  key="JoeLoyalty.Unverified.Headline-text"
                  id="JoeLoyalty.Unverified.Headline"
                  defaultMessage="Sind Sie bereits Kunde bei jö?"
                />
              </div>
              <div className="text-size-body-sm mb-1.5">
                <FormattedMessage
                  key="JoeLoyalty.Unverified.Message-text"
                  id="JoeLoyalty.Unverified.Message"
                  defaultMessage="Auf Schritt 5 können Sie Ihr Konto ganz einfach verknüpfen, Ös sammeln und viel Geld sparen."
                />
              </div>
              <div className="border-color-grey rounded-s border p-2">
                <div className="flex flex-col">
                  <p className="text-size-body-sm text-center font-semibold">
                    <FormattedMessage
                      key="JoeLoyalty.Advantages.Headline-text"
                      id="JoeLoyalty.Advantages.Headline"
                      defaultMessage="jö Vorteile im Überblick"
                    />
                  </p>
                  <ul className="mt-1 pl-0">
                    <li className="mb-0.438rem flex flex-row justify-start gap-1.5">
                      <div className="icon-emp-checkmark size-3.5 shrink-0 fill-[#d6ab53] text-[#d6ab53]" />
                      <div className="text-size-body-sm">
                        <FormattedMessage
                          key="JoeLoyalty.Advantages.First-text"
                          id="JoeLoyalty.Advantages.First"
                          defaultMessage="Bei jedem Einkauf Ös sammeln:{break}
                      <strong>Pro 2€ erhalten Sie ein Ö</strong>"
                          values={{
                            break: <br />,
                            strong: (chunks) => (
                              <span className="text-size-body-sm font-semibold">{chunks}</span>
                            ),
                          }}
                        />
                      </div>
                    </li>
                    <li className="mb-0.438rem flex flex-row justify-start gap-1.5">
                      <div className="icon-emp-checkmark size-3.5 shrink-0 fill-[#d6ab53] text-[#d6ab53]" />
                      <div className="text-size-body-sm">
                        <FormattedMessage
                          key="JoeLoyalty.Advantages.Second-text"
                          id="JoeLoyalty.Advantages.Second"
                          defaultMessage="Durch die Eingabe von Aktionscodes mehrfach Ös erhalten."
                        />
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              {joeUrl && (
                <div className="mt-2 flex flex-row text-center text-[0.813rem]">
                  <span className="icon-emp-joe mr-1 size-2" />
                  <FormattedMessage
                    key="JoeLoyalty.Unverified.Register-text"
                    id="JoeLoyalty.Unverified.Register"
                    defaultMessage="Noch kein jö Mitgleid? <joeLink>Jetzt registrieren</joeLink>"
                    values={{
                      joeLink: (chunks: React.ReactNode) => (
                        <a
                          href={getLocalizedPath(joeUrl, locale)}
                          target="_blank"
                          className="text-color-text-dark ml-1 underline"
                          rel="noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
